import { inject, Injectable, InjectionToken } from '@angular/core';
import { ConfigModel, GallerySlideComponentType, GsStructComponent, PageComponentType } from '../../models';
import { SchemaService } from '../forms';
import { BasicComponentDefinition, ComponentsHelper } from './components.service';

export type GallerySlideComponentGroup = 'basic';

export interface GallerySlideComponentDefinition extends BasicComponentDefinition<GallerySlideComponentGroup> {
  /** Is this component suited with a userControl in the backend and maby also has some backend-settings? */
  backendComponent: boolean;
  /** Is this component still in development and shouldnt be used yet? (Disabled for adding newly into pages, components already on page will work) */
  wip?: boolean;
}

/**
 * Token providing the gallery-slide-component registry. Wired via
 * `GALLERY_SLIDE_COMPONENTS_REGISTRY_PROVIDER` in each app config. Split out to keep
 * template imports off the services barrel chain (SSR cycle fix — see page.components.service.ts).
 */
export const GALLERY_SLIDE_COMPONENTS_REGISTRY = new InjectionToken<
  Record<GallerySlideComponentType, GallerySlideComponentDefinition>
>('GALLERY_SLIDE_COMPONENTS_REGISTRY', {
  factory: () => ({}) as Record<GallerySlideComponentType, GallerySlideComponentDefinition>
});

@Injectable({
  providedIn: 'root'
})
export class GallerySlideComponentsHelper extends ComponentsHelper<
  GallerySlideComponentType,
  GallerySlideComponentDefinition,
  GallerySlideComponentGroup
> {
  // Services
  /** The components to display in the sidemenu */
  components: Record<GallerySlideComponentType, GallerySlideComponentDefinition> = inject(GALLERY_SLIDE_COMPONENTS_REGISTRY);
  /** The translation prefix for the groups of the components, to display the groupnames in the right language */
  override groupTranslationPrefix: string = 'gallerySlideComponents.groups.';
  /** The priority of the groups to display them in the right order in the sidemenu (lower number = higher priority) */
  override groupPriorities = {
    basic: 1
  };

  // Methods
  /**
   * Get the default settings for a component
   * @param type of the component
   * @param config the config to get the default settings from
   * @returns the default settings for the component
   */
  getComponentDefaultSettings(type: GallerySlideComponentType, config?: ConfigModel): GsStructComponent<unknown> | undefined {
    const configDefaultSettings: GsStructComponent<unknown> | undefined = config?.components ? config.components[type] : undefined;
    const data = this.components[type]?.formSchema?.fields?.reduce((acc, field) => {
      if (SchemaService.isGsFormController(field)) {
        if (field.type === 'group') return acc;
        const configDefault = configDefaultSettings
          ? configDefaultSettings[field.formControlName as keyof GsStructComponent<unknown>]
          : undefined;
        if (configDefault != null || field.default != null) {
          (acc as unknown as Record<string, unknown>)[field.formControlName] = configDefault ?? field.default;
        }
      }
      return acc;
    }, {} as GsStructComponent<any>);
    return data;
  }

  /**
   * Check if the component is a backend component, which means it has a userControl in the backend and maby also some backend-settings
   * @param type of the component
   * @returns true if its a backend component, false if not or the component doesnt exist
   */
  isBackendComponent(type: PageComponentType): boolean {
    if (type in this.components) return this.components[type as GallerySlideComponentType]?.backendComponent ?? false;
    return false;
  }
}
